import React, {useMemo} from 'react';
import {ATTRIBUTE_SYSTEM_ID} from '@netx/core-attribute/constants';
import {getCurrentPortalProp} from '@netx/core-cms-next/utils/getCurrentPortalProp';
import {isBrandPortal} from '@netx/core-cms-next/utils/isBrandPortal';
import Dropdown, {AttributeDropdownProps} from '../../components/Dropdown';
import {useAttributeProvider} from '../../context';
import {type AttributeTemplateObjectType} from '../../schema';

export type AttributeDropdownContainerProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> = Omit<
	AttributeDropdownProps<Multiple, DisableClearable>,
	'attributes' | 'options'
> & {
	omitReadOnly?: boolean;
	omitSystem?: boolean;
	objectType?: AttributeTemplateObjectType;
	readOnly?: boolean;
	omitIds?: number[];
};

export const AttributeDropdownContainerPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		omitReadOnly,
		omitSystem,
		objectType,
		omitIds,
		...props
	}: AttributeDropdownContainerProps<Multiple, DisableClearable>,
	ref: any,
) => {
	const attributeProvider = useAttributeProvider();

	const attributes = useMemo(() => {
		const allAttributes = attributeProvider.getAll({
			omitSystem,
			omitReadOnly,
			objectType,
			omitIds,
		});

		if (!isBrandPortal()) return allAttributes;

		const portalAttributeIds =
			getCurrentPortalProp('attributes')?.map(
				(attr) => attr.attributeTemplateId,
			) ?? [];
		return allAttributes.filter((attribute) =>
			portalAttributeIds.includes(attribute.attributeid),
		);
	}, [omitSystem, omitReadOnly, objectType, omitIds]);

	return (
		<Dropdown<Multiple, DisableClearable>
			ref={ref}
			attributes={attributes}
			{...props}
		/>
	);
};

export const AttributeDropdownContainer = React.forwardRef(
	AttributeDropdownContainerPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: AttributeDropdownContainerProps<Multiple, DisableClearable>,
	ref: any,
) => JSX.Element;

export default AttributeDropdownContainer;
